import type { ReactNode } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import { neutral } from "app/themes/colors";
import CircularProgress from "components/circular-progress";
import { MONO_FONT_FAMILY } from "constants/common";
import { formatAtSeconds, speakerLabel } from "utils/transcript-lines.util";

import useScribeTranscriptLines from "./use-scribe-transcript-lines.hook";

/**
 * Contrast-verified. The `neutral` ramp has nothing usable here: `neutral[600]`
 * measures ~3.1:1 on white and `neutral[700]` 3.76:1, both under the 4.5:1 AA
 * floor for text this size, and the next stop (`text.secondary`) is near-black
 * and reads as loud as the transcript itself. Follow Ups declares the same hex
 * for the same reason — there is no muted token in the theme.
 */
const MUTED_COLOR = "#5C6260"; // 5.81:1

/**
 * Every style here is a module-scope `styled()` with a static object, so each
 * serializes once at import rather than per element. A long visit renders
 * thousands of rows, and an `sx` prop on each would run `styleFunctionSx` for
 * every one of them on every render — that, not the DOM node count, is the cost.
 */
const TranscriptBox = styled("div")({
  border: `1px solid ${neutral[200]}`,
  borderRadius: 8,
  padding: 10
});

const Line = styled("div")({
  display: "flex",
  gap: 12,
  padding: "7px 11px",
  fontSize: "0.94rem",
  lineHeight: 1.5,
  // Lets the browser skip layout and paint for rows that are scrolled out of
  // view. Purely an optimization — removing these two lines is always safe.
  contentVisibility: "auto",
  containIntrinsicSize: "auto 40px"
});

const Speaker = styled("span")({
  flex: "none",
  width: 72,
  paddingTop: 2,
  fontFamily: MONO_FONT_FAMILY,
  fontSize: "0.75rem",
  fontWeight: 700,
  color: MUTED_COLOR
});

const LineText = styled("span")({ flex: 1, minWidth: 0 });

const Timestamp = styled("span")({
  flex: "none",
  paddingTop: 2,
  fontFamily: MONO_FONT_FAMILY,
  fontSize: "0.75rem",
  color: MUTED_COLOR
});

interface ScribeTranscriptLinesProps {
  scribeId: number;
  /**
   * Rendered on a settled failure or an unsplittable transcript. This is the
   * flag-OFF node, so a hiccup in a secondary endpoint never costs the
   * clinician the transcript itself.
   */
  fallback: ReactNode;
}

export default function ScribeTranscriptLines({
  scribeId,
  fallback
}: ScribeTranscriptLinesProps) {
  const { lines, isLoading, isError, truncation } =
    useScribeTranscriptLines(scribeId);

  if (isLoading) return <CircularProgress />;

  if (isError || lines.length === 0) {
    return (
      <>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={2}
        >
          Formatted view isn&apos;t available for this recording.
        </Typography>
        {fallback}
      </>
    );
  }

  return (
    <Box mt={2}>
      <TranscriptBox>
        {lines.map((line) => (
          <Line key={line.line}>
            {/* A placeholder speaker degrades to the line number rather than
                inventing a clinical role — diarisation ids are never one. */}
            <Speaker>{speakerLabel(line.speaker) ?? `Line ${line.line}`}</Speaker>
            <LineText>{line.text}</LineText>
            <Timestamp>{formatAtSeconds(line.at_seconds)}</Timestamp>
          </Line>
        ))}
      </TranscriptBox>
      {truncation && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={1}
        >
          {truncation === "capped"
            ? `This recording is unusually long — only the first ${lines.length.toLocaleString()} lines are shown.`
            : "Some of this transcript could not be loaded."}
        </Typography>
      )}
    </Box>
  );
}
